import { useEffect, useRef, useState } from 'react';
import { Music2, Upload, Trash2, Loader2, Play, Pause, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { BACKEND_URL } from '../../lib/backend';

function fmtSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

export function InstrumentalPanel({ instrumentals, onRefresh, engine, artistId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  // ---- preview playback (simple <audio>, separate from the DAW engine) ----
  const previewRef = useRef(null);
  const [previewId, setPreviewId] = useState(null);

  const stopPreview = () => {
    const a = previewRef.current;
    if (a) { a.pause(); a.src = ''; }
    previewRef.current = null;
    setPreviewId(null);
  };

  const handlePreview = (inst) => {
    if (previewId === inst.id) { stopPreview(); return; }
    stopPreview();
    const audio = new Audio(`${BACKEND_URL}${inst.url}`);
    audio.onended = () => stopPreview();
    audio.onerror = () => { toast.error('Could not preview that track'); stopPreview(); };
    audio.play().catch(() => { toast.error('Could not preview that track'); stopPreview(); });
    previewRef.current = audio;
    setPreviewId(inst.id);
  };

  // Stop the preview when the artist leaves the Studio.
  useEffect(() => () => {
    const a = previewRef.current;
    if (a) { a.pause(); a.src = ''; }
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      await api.uploadInstrumental(fd);
      toast.success('Instrumental uploaded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleLoad = async (inst) => {
    stopPreview();
    setLoadingId(inst.id);
    try {
      await engine.loadInstrumentalFromUrl(`${BACKEND_URL}${inst.url}`, { title: inst.title, id: inst.id });
      toast.success(`Loaded "${inst.title}" onto the timeline`);
    } catch (e) {
      toast.error('Could not load track');
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (inst) => {
    if (!window.confirm(`Delete "${inst.title}"?`)) return;
    try {
      await api.deleteInstrumental(inst.id);
      toast.success('Deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="instrumental-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Music2 className="w-4 h-4 text-lime" />Instrumentals
        </h3>
        <Button size="sm" variant="outline" className="border-[#333] h-8 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-instrumental-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1.5" />Upload</>}
        </Button>
        <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.aif,.aiff" className="hidden" onChange={handleUpload} data-testid="instrumental-file-input" />
      </div>

      {instrumentals.length === 0 ? (
        <p className="text-xs text-[#666] py-4 text-center">
          No instrumentals yet. Upload one from your phone, or check back — new riddims drop here.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {instrumentals.map((inst) => (
            <div key={inst.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#111] border border-[#222] hover:border-lime/30 transition-colors group" data-testid={`instrumental-row-${inst.id}`}>
              <button
                onClick={() => handlePreview(inst)}
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${previewId === inst.id ? 'bg-lime text-black' : 'bg-lime/10 text-lime hover:bg-lime/20'}`}
                title={previewId === inst.id ? 'Stop preview' : 'Preview this riddim'}
                data-testid={`preview-instrumental-${inst.id}`}
              >
                {previewId === inst.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => handleLoad(inst)}
                disabled={loadingId === inst.id || engine.loadingTrack}
                className="w-7 h-7 rounded-full bg-lime/10 text-lime flex items-center justify-center hover:bg-lime/20 shrink-0"
                title="Load onto timeline"
                data-testid={`load-instrumental-${inst.id}`}
              >
                {loadingId === inst.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{inst.title}</p>
                <p className="text-[10px] text-[#666]">
                  {inst.artist_id ? 'My upload' : 'Tune4Tune library'} {fmtSize(inst.size) && `• ${fmtSize(inst.size)}`}
                </p>
              </div>
              {inst.artist_id === artistId && (
                <button onClick={() => handleDelete(inst)} className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-400 p-1" data-testid={`delete-instrumental-${inst.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
